import { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Bug, Trash2 } from 'lucide-react';

import { sessionState } from '@chainlit/react-client';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  debugInfoOpenState,
  debugInfoState,
  type DebugInfoEntry
} from '@/state/debugInfo';

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

export default function DebugInfoButton() {
  const session = useRecoilValue(sessionState);
  const [entries, setEntries] = useRecoilState(debugInfoState);
  const [open, setOpen] = useRecoilState(debugInfoOpenState);

  useEffect(() => {
    const socket = session?.socket;
    if (!socket) return;
    const handler = (data: DebugInfoEntry) => {
      if (!data || typeof data.content !== 'string' || !data.content.trim()) {
        return;
      }
      setEntries((prev) => [
        ...prev,
        {
          recorded_at: data.recorded_at || new Date().toISOString(),
          content: data.content
        }
      ]);
    };
    socket.on('debug_info_append', handler);
    return () => {
      socket.off('debug_info_append', handler);
    };
  }, [session?.socket, setEntries]);

  const hasEntries = entries.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-no-maximize
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          className={cn(
            'flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium uppercase tracking-wide transition-colors',
            hasEntries
              ? 'text-primary hover:bg-accent'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
          )}
          title="Show per-turn thinking and tool traces"
          aria-label="Debug info"
        >
          <Bug className="h-3 w-3" aria-hidden="true" />
          <span>Debug</span>
          {hasEntries ? (
            <span className="rounded-full bg-primary/20 px-1 text-[9px] font-semibold text-primary">
              {entries.length}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="flex h-[60vh] w-[420px] max-w-[90vw] flex-col p-0"
      >
        <div className="flex flex-none items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Debug Info
          </span>
          <button
            type="button"
            onClick={() => setEntries([])}
            disabled={!hasEntries}
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
              hasEntries
                ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
                : 'text-muted-foreground/40 cursor-not-allowed'
            )}
            title="Clear debug entries"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" />
            Clear
          </button>
        </div>
        <ScrollArea type="always" className="min-h-0 flex-1">
          <div className="flex flex-col gap-3 p-3">
            {hasEntries ? (
              entries.map((entry, idx) => (
                <div
                  key={`${entry.recorded_at}-${idx}`}
                  className="rounded border border-border bg-muted/40 p-2"
                >
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {formatTimestamp(entry.recorded_at)}
                  </div>
                  <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-foreground">
                    {entry.content}
                  </pre>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-xs text-muted-foreground">
                No debug entries yet. Send a prompt and the turn's thinking will land here.
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
